import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Gift, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

const CATEGORIES = ["Consultas","Odontologia","Fisioterapia","Nutrição","Psicologia","Dermatologia","Exames","Farmácia","Bem-estar","Outros"];
const CITIES = ["Santos","Praia Grande","São Vicente","Guarujá","Cubatão","Bertioga","Mongaguá","Itanhaém","Peruíbe"];

export function ProBenefitsManager() {
  const { user } = useAuth();
  const role = user?.role;
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [f, setF] = useState({title:"", description:"", category:"Consultas", city:"", condition:"", validity:"", image_url:""});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      if (role === "professional") {
        const meRes = await api.get("/professionals/me");
        setMe(meRes.data);
        setF(fv => ({...fv, city: fv.city || meRes.data?.city || ""}));
      } else {
        setF(fv => ({...fv, city: fv.city || user?.city || ""}));
      }
      const all = await api.get("/professionals/me/benefits");
      setItems(all.data);
    } catch (err) {
      toast.error("Não foi possível carregar seus benefícios.");
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || !f.description) return toast.error("Preencha título e descrição");
    setSaving(true);
    try {
      await api.post("/benefits", {
        ...f,
        partner_id: me?.id,
        partner_name: me?.name || user?.name || "Parceiro",
      });
      toast.success("Benefício enviado! Será publicado após aprovação do admin.");
      setF({title:"", description:"", category:"Consultas", city: me?.city || "", condition:"", validity:"", image_url:""});
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="pro-benefits-manager">
      <Card className="p-6 gs-shadow">
        <div className="flex items-center gap-2 mb-1"><Gift className="w-5 h-5 text-[#FF7D54]"/><div className="text-xs uppercase tracking-widest text-[#0D7C66] font-semibold">Descontos & Benefícios</div></div>
        <h2 className="text-2xl font-extrabold" style={{fontFamily:"Plus Jakarta Sans"}}>Cadastrar novo benefício</h2>
        <p className="text-sm text-slate-500 mt-1">Ao publicar, o benefício passará por aprovação administrativa antes de aparecer para os associados.</p>

        <form onSubmit={submit} className="mt-5 grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500">Título *</label>
            <Input data-testid="benef-title" value={f.title} onChange={e=>setF({...f,title:e.target.value})} placeholder="Ex.: 20% off em consultas cardiológicas"/>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500">Descrição *</label>
            <Textarea data-testid="benef-description" rows={3} value={f.description} onChange={e=>setF({...f,description:e.target.value})} placeholder="Detalhe o desconto/vantagem oferecida aos associados"/>
          </div>
          <div>
            <label className="text-xs text-slate-500">Categoria</label>
            <select data-testid="benef-category" value={f.category} onChange={e=>setF({...f,category:e.target.value})} className="w-full h-10 rounded-md border border-input px-3 text-sm">
              {CATEGORIES.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Cidade</label>
            <select data-testid="benef-city" value={f.city} onChange={e=>setF({...f,city:e.target.value})} className="w-full h-10 rounded-md border border-input px-3 text-sm">
              <option value="">Todas</option>
              {CITIES.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Condição</label>
            <Input data-testid="benef-condition" value={f.condition} onChange={e=>setF({...f,condition:e.target.value})} placeholder="Ex.: Apresentar carteira digital"/>
          </div>
          <div>
            <label className="text-xs text-slate-500">Validade</label>
            <Input data-testid="benef-validity" value={f.validity} onChange={e=>setF({...f,validity:e.target.value})} placeholder="Ex.: 31/12/2026"/>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500">Imagem (URL, opcional)</label>
            <Input data-testid="benef-image" value={f.image_url} onChange={e=>setF({...f,image_url:e.target.value})} placeholder="https://…"/>
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button data-testid="benef-submit" type="submit" disabled={saving} className="bg-[#0D7C66] hover:bg-[#074E41]"><Plus className="w-4 h-4 mr-1"/>{saving?"Enviando...":"Publicar benefício"}</Button>
          </div>
        </form>
      </Card>

      <Card className="p-6 gs-shadow">
        <div className="font-bold mb-4">Meus benefícios cadastrados</div>
        {items.length === 0 && <div className="text-sm text-slate-500 text-center py-6">Você ainda não cadastrou benefícios. Use o formulário acima.</div>}
        <div className="space-y-2">
          {items.map(b => (
            <div key={b.id} className="flex items-start justify-between gap-3 border-b border-slate-100 py-3" data-testid={`my-benef-${b.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-[#E6F4F1] text-[#0D7C66] border-0">{b.category}</Badge>
                  <b>{b.title}</b>
                </div>
                <div className="text-sm text-slate-600 mt-1">{b.description}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {b.city && <>Cidade: {b.city} · </>}
                  {b.condition && <>Condição: {b.condition} · </>}
                  {b.validity && <>Validade: {b.validity}</>}
                </div>
              </div>
              <Badge className={b.status==="active"?"bg-[#E6F4F1] text-[#0D7C66] border-0":"bg-amber-100 text-amber-800 border-0"}>{b.status==="active"?"Publicado":"Aguardando aprovação"}</Badge>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
